// RECOVERY FROM A STOPPED ACCOUNT STREAM.
//
// `sseSubscribe` under `always` re-dials a drop, a 5xx and a recycle itself; it
// RETURNS on a refusal (any 4xx), an `ended` and a `crashed`, because hammering
// the same request a few seconds later changes nothing. That stop used to be the
// end: the shell's one connection parked in `error` for the life of the tab, so
// one transient 403 while an api pod rolled sent every live view in that tab
// back to 30 s polls until a reload, silently.
//
// The fix is a SECOND, slower posture over the stopped loop — not a loosening of
// the loop's own. A stopped stream is re-dialled once, on whichever comes first:
//   - the browser saying the network is back (`online`),
//   - the tab becoming visible again (a person is looking; the poll is not enough),
//   - a timer at the ladder's CAP (`SSE_BACKOFF_CAP_MS`, equal-jittered: 30-60 s),
//     so a tab nobody touches still heals after a deploy, at a pace that cannot
//     become a herd.
// What it never does: re-dial a 401 (the session is gone and `markSessionExpired`
// has already routed to sign-in), or put a `crashed` loop on the timer — that is
// a bug in this bundle replaying the snapshot that crashed it, so only a person
// coming back (online / visible) earns another try, never an unattended loop
// reporting the same crash every minute.

import type { SseOutcome } from '../sse-subscribe';
import { reconnectDelayMs, SSE_BACKOFF_CAP_MS } from '../sseBackoff';

/** How a stopped stream may come back. `null`: it may not (a dead session). */
export function recoveryFor(
  outcome: SseOutcome,
  random: () => number,
): { readonly timerMs: number | null } | null {
  if (outcome.kind === 'refused' && outcome.status === 401) return null;
  if (outcome.kind === 'crashed') return { timerMs: null };
  return {
    timerMs: reconnectDelayMs({
      attempt: 0,
      baseMs: SSE_BACKOFF_CAP_MS,
      retryAfterMs: null,
      random,
    }),
  };
}

/**
 * Call `redial` ONCE, on the first of `online`, the document turning visible, or
 * `timerMs` (when not null). Returns the disarm, which the caller's cleanup runs
 * — an unmount, an org switch or the re-dial itself must leave no listener and
 * no timer behind.
 */
export function armRedial(redial: () => void, timerMs: number | null): () => void {
  let armed = true;
  const fire = (): void => {
    if (!armed) return;
    disarm();
    redial();
  };
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') fire();
  };
  const timer = timerMs === null ? undefined : setTimeout(fire, timerMs);
  window.addEventListener('online', fire);
  document.addEventListener('visibilitychange', onVisible);
  function disarm(): void {
    armed = false;
    if (timer !== undefined) clearTimeout(timer);
    window.removeEventListener('online', fire);
    document.removeEventListener('visibilitychange', onVisible);
  }
  return disarm;
}
